import { useRef } from 'react';
import {
  CircleAlert,
  FileJson,
  Layers3,
  Loader2,
  Save,
  Upload,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  TeamAiSkillCategory,
  TeamAiSkillAgent,
  TeamAiSkillCreateInput,
  TeamAiSkillScope,
} from './admin-team-ai-skills.types';
import {
  CATEGORY_OPTIONS,
  MAX_SKILL_CONTENT_LENGTH,
  SCOPE_OPTIONS,
} from './admin-team-ai-skills.utils';

export function SkillDraftDialog({
  open,
  mode,
  form,
  busy,
  onOpenChange,
  onFormChange,
  onImportFile,
  onSubmit,
}: {
  open: boolean;
  mode: 'create' | 'import';
  form: TeamAiSkillCreateInput;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onFormChange: (form: TeamAiSkillCreateInput) => void;
  onImportFile: (file: File) => void;
  onSubmit: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'import'
              ? 'Import Team AI skill'
              : 'Create Team AI skill'}
          </DialogTitle>
          <DialogDescription>
            Skills cannot grant credentials, validation, merge, or completion
            authority. Executable coder skills run in an isolated sandbox.
          </DialogDescription>
        </DialogHeader>
        {mode === 'import' && (
          <div className="flex items-center gap-3 border bg-muted/40 p-3">
            <FileJson className="h-5 w-5 text-muted-foreground" />
            <p className="flex-1 text-sm text-muted-foreground">
              Load a JSON object with exact key, name, description, agent,
              category, content, and executable fields. Executable coder skills
              must use the bounded manifest contract.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onImportFile(file);
                event.target.value = '';
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload />
              Choose file
            </Button>
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="skill-key">Key</Label>
            <Input
              id="skill-key"
              value={form.key}
              placeholder="frontend.react"
              onChange={(event) =>
                onFormChange({ ...form, key: event.target.value })
              }
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, numbers, dots, underscores, or dashes.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="skill-name">Name</Label>
            <Input
              id="skill-name"
              value={form.name}
              placeholder="React planning conventions"
              onChange={(event) =>
                onFormChange({ ...form, name: event.target.value })
              }
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="skill-description">Description</Label>
            <Input
              id="skill-description"
              value={form.description}
              placeholder="When and why the selected agent should apply this skill"
              onChange={(event) =>
                onFormChange({ ...form, description: event.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="skill-agent">Agent</Label>
            <Select
              value={form.agent}
              onValueChange={(value) =>
                onFormChange({
                  ...form,
                  agent: value as TeamAiSkillAgent,
                  executable: value === 'pm' ? null : form.executable,
                })
              }
            >
              <SelectTrigger id="skill-agent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pm">PM</SelectItem>
                <SelectItem value="coder">Coding AI</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="skill-category">Category</Label>
            <Select
              value={form.category}
              onValueChange={(value) =>
                onFormChange({
                  ...form,
                  category: value as TeamAiSkillCategory,
                })
              }
            >
              <SelectTrigger id="skill-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="skill-content">Instructions</Label>
              <span className="text-xs text-muted-foreground">
                {form.content.length}/{MAX_SKILL_CONTENT_LENGTH}
              </span>
            </div>
            <textarea
              id="skill-content"
              className="min-h-56 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 font-mono text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              maxLength={MAX_SKILL_CONTENT_LENGTH}
              value={form.content}
              onChange={(event) =>
                onFormChange({ ...form, content: event.target.value })
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={onSubmit}>
            {busy ? <Loader2 className="animate-spin" /> : <Save />}
            Save draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SkillVersionDialog({
  open,
  mode,
  content,
  busy,
  onOpenChange,
  onContentChange,
  onSubmit,
}: {
  open: boolean;
  mode: 'edit' | 'new';
  content: string;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onContentChange: (content: string) => void;
  onSubmit: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'Edit draft version' : 'Create new version'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'edit'
              ? 'Saving invalidates previous evaluation evidence for this draft.'
              : 'The selected version is copied into a new editable draft.'}
          </DialogDescription>
        </DialogHeader>
        {mode === 'edit' && (
          <Alert>
            <CircleAlert className="h-4 w-4" />
            <AlertTitle>Evaluation will be reset</AlertTitle>
            <AlertDescription>
              Any content change requires a new platform evaluation before
              publication.
            </AlertDescription>
          </Alert>
        )}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="version-content">Instructions</Label>
            <span className="text-xs text-muted-foreground">
              {content.length}/{MAX_SKILL_CONTENT_LENGTH}
            </span>
          </div>
          <textarea
            id="version-content"
            className="min-h-[26rem] w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 font-mono text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            maxLength={MAX_SKILL_CONTENT_LENGTH}
            value={content}
            onChange={(event) => onContentChange(event.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!content.trim() || busy} onClick={onSubmit}>
            {busy ? <Loader2 className="animate-spin" /> : <Save />}
            {mode === 'edit' ? 'Save draft' : 'Create version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SkillAssignmentDialog({
  open,
  scopeType,
  scopeKey,
  priority,
  busy,
  onOpenChange,
  onScopeTypeChange,
  onScopeKeyChange,
  onPriorityChange,
  onSubmit,
}: {
  open: boolean;
  scopeType: TeamAiSkillScope;
  scopeKey: string;
  priority: string;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onScopeTypeChange: (scope: TeamAiSkillScope) => void;
  onScopeKeyChange: (scopeKey: string) => void;
  onPriorityChange: (priority: string) => void;
  onSubmit: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign published version</DialogTitle>
          <DialogDescription>
            Assigning an earlier published version is the rollback mechanism.
            More specific scopes win during composition.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="assignment-scope">Scope</Label>
            <Select
              value={scopeType}
              onValueChange={(value) =>
                onScopeTypeChange(value as TeamAiSkillScope)
              }
            >
              <SelectTrigger id="assignment-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_OPTIONS.map((scope) => (
                  <SelectItem key={scope} value={scope}>
                    {scope}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {scopeType !== 'platform' && (
            <div className="space-y-2">
              <Label htmlFor="assignment-key">Scope key</Label>
              <Input
                id="assignment-key"
                value={scopeKey}
                maxLength={100}
                placeholder={`${scopeType} identifier`}
                onChange={(event) => onScopeKeyChange(event.target.value)}
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="assignment-priority">Priority</Label>
            <Input
              id="assignment-priority"
              type="number"
              min={0}
              max={1000}
              step={1}
              value={priority}
              onChange={(event) => onPriorityChange(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Higher priority wins between assignments at the same scope
              specificity.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={onSubmit}>
            {busy ? <Loader2 className="animate-spin" /> : <Layers3 />}
            Save assignment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
